/**
 * Utility functions for patient intake display
 */

import type { IntakeResponse, CategorizedResponses } from '@jema-manager/shared';

/**
 * Format question ID: capitalize first letter, add space before capitals, replace underscores
 */
export const formatQuestionId = (id: string): string => {
    return id
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/^./, (char) => char.toUpperCase());
};

/**
 * Categorize responses into logical groups for display
 */
export const categorizeResponses = (responses: IntakeResponse[]): CategorizedResponses => {
    const lower = (id: string) => id.toLowerCase();
    const selected = new Set<string>();

    const pick = (predicate: (id: string) => boolean) =>
        responses.filter((response) => {
            const id = lower(response.questionId);
            if (predicate(id)) {
                selected.add(response.questionId);
                return true;
            }
            return false;
        });

    const procedures = pick(
        (id) =>
            id.includes('procedure') ||
            id.includes('treatment') ||
            id.includes('symptom') ||
            id.includes('concern')
    );
    const goals = pick((id) => id.includes('goal') || id.includes('expectation'));
    const medical = pick(
        (id) =>
            id.includes('medical') ||
            id.includes('condition') ||
            id.includes('medication') ||
            id.includes('allerg') ||
            id.includes('surg')
    );
    const travel = pick(
        (id) => id.includes('travel') || id.includes('flight') || id.includes('stay')
    );

    const other = responses.filter((response) => !selected.has(response.questionId));

    return { procedures, goals, medical, travel, other };
};

/**
 * Check if a question ID is related to photo/media questions
 */
export const isPhotoResponse = (questionId: string): boolean => {
    const photoKeywords = ['photo', 'image', 'picture', 'upload', 'media'];
    const lowerQuestionId = questionId.toLowerCase();
    return photoKeywords.some((keyword) => lowerQuestionId.includes(keyword));
};

/**
 * Default photo positions for dental intake forms
 */
export const DEFAULT_PHOTO_POSITIONS = [
    { id: 'upper_occlusal', labelKey: 'Upper Occlusal', descriptionKey: 'Upper teeth occlusal view' },
    { id: 'frontal_retracted_apart', labelKey: 'Frontal Retracted Apart', descriptionKey: 'Front view with lips pulled back, teeth apart' },
    { id: 'lower_occlusal', labelKey: 'Lower Occlusal', descriptionKey: 'Lower teeth occlusal view' },
    { id: 'right_buccal', labelKey: 'Right Buccal', descriptionKey: 'Right side bite view' },
    { id: 'frontal_in_occlusion', labelKey: 'Frontal In Occlusion', descriptionKey: 'Front view with teeth together' },
    { id: 'left_buccal', labelKey: 'Left Buccal', descriptionKey: 'Left side bite view' },
] as const;

export const DEFAULT_PHOTO_POSITION_IDS = DEFAULT_PHOTO_POSITIONS.map((p) => p.id) as string[];

/**
 * Check if a photo position is one of the default required positions
 */
export const isDefaultPhotoPosition = (photoPosition?: string | null): boolean => {
    if (!photoPosition) return false;
    return DEFAULT_PHOTO_POSITION_IDS.includes(photoPosition);
};

/**
 * Get sort order for photo positions (default positions first, in order)
 */
export const getPhotoPositionOrder = (photoPosition?: string | null): number => {
    const index = photoPosition ? DEFAULT_PHOTO_POSITION_IDS.indexOf(photoPosition) : -1;
    return index === -1 ? DEFAULT_PHOTO_POSITION_IDS.length : index;
};

/**
 * Get readable label for a photo position
 */
export const getPhotoPositionLabel = (position?: string | null): string => {
    if (!position) return 'Additional Photo';
    const defaultPosition = DEFAULT_PHOTO_POSITIONS.find((p) => p.id === position);
    if (defaultPosition) return defaultPosition.labelKey;
    return 'Additional Photo';
};

/**
 * Format file size in human readable format
 */
export const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'Size unknown';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
